import React, { createContext, useContext, useState, useEffect } from 'react'
import { ConfigProvider, theme } from 'antd'
import ptBR from 'antd/locale/pt_BR'

// Criar o contexto do tema
const ThemeContext = createContext()

// Hook para usar o contexto do tema
export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider')
  }
  return context
}

// Temas disponíveis
const themes = {
  dark: {
    algorithm: theme.darkAlgorithm,
    token: {
      colorPrimary: '#1890ff',
      colorBgBase: '#000000',
      colorBgContainer: '#111111',
      colorBgElevated: '#1a1a1a',
      colorText: '#ffffff',
      colorTextSecondary: '#cccccc',
      colorBorder: '#333333',
      colorBorderSecondary: '#444444',
    },
    components: {
      Layout: {
        bodyBg: '#000000',
        headerBg: '#111111',
        siderBg: '#111111',
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: '#1890ff',
        itemHoverBg: '#333333',
        itemColor: '#ffffff',
        itemSelectedColor: '#ffffff',
      },
      Card: {
        colorBgContainer: '#111111',
        colorBorderSecondary: '#333333',
      },
      Table: {
        colorBgContainer: '#111111',
        headerBg: '#222222',
        headerColor: '#ffffff',
        rowHoverBg: '#1a1a1a',
      },
      Input: {
        colorBgContainer: '#1e293b',
        colorBorder: '#475569',
        colorText: '#e2e8f0',
        colorTextPlaceholder: '#94a3b8',
        // Configurações para desabilitar autofill styling
        autocompleteBg: '#1e293b',
        activeBorderColor: '#475569',
        hoverBorderColor: '#64748b',
      },
      Select: {
        colorBgContainer: '#1e293b',
        colorBorder: '#475569',
        colorText: '#e2e8f0',
      },
      Modal: {
        contentBg: '#111111',
        headerBg: '#111111',
      },
      Button: {
        colorBgContainer: 'transparent',
      },
    },
  },
  light: {
    algorithm: theme.defaultAlgorithm,
    token: {
      colorPrimary: '#52c41a', // Verde principal
      colorPrimaryHover: '#73d13d', // Verde hover
      colorPrimaryActive: '#389e0d', // Verde ativo
      colorBgBase: '#f6ffed', // Fundo base verde muito claro
      colorBgContainer: '#ffffff', // Containers brancos
      colorBgElevated: '#ffffff', // Elementos elevados brancos
      colorText: '#262626', // Texto escuro
      colorTextSecondary: '#595959', // Texto secundário
      colorBorder: '#d9f7be', // Bordas verde claro
      colorBorderSecondary: '#f6ffed', // Bordas secundárias
      colorSuccess: '#52c41a', // Verde sucesso
      colorWarning: '#faad14', // Amarelo aviso
      colorError: '#ff4d4f', // Vermelho erro
      colorInfo: '#1890ff', // Azul informação
    },
    components: {
      Layout: {
        bodyBg: '#f6ffed', // Fundo geral verde muito claro
        headerBg: '#52c41a', // Header verde
        siderBg: '#ffffff', // Sidebar branca
        triggerBg: '#73d13d', // Trigger verde claro
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: '#d9f7be', // Item selecionado verde claro
        itemHoverBg: '#f6ffed', // Hover verde muito claro
        itemColor: '#262626', // Texto do menu escuro
        itemSelectedColor: '#389e0d', // Texto selecionado verde escuro
        itemActiveBg: '#d9f7be',
      },
      Card: {
        colorBgContainer: '#ffffff', // Cards brancos
        colorBorderSecondary: '#d9f7be', // Bordas verde claro
        headerBg: '#f6ffed', // Header do card verde muito claro
      },
      Table: {
        colorBgContainer: '#ffffff', // Tabela branca
        headerBg: '#f6ffed', // Header da tabela verde muito claro
        headerColor: '#262626', // Texto do header escuro
        rowHoverBg: '#f6ffed', // Hover das linhas verde muito claro
        borderColor: '#d9f7be', // Bordas verde claro
      },
      Input: {
        colorBgContainer: '#ffffff', // Input branco
        colorBorder: '#d9f7be', // Borda verde claro
        colorText: '#262626', // Texto escuro
        colorTextPlaceholder: '#8c8c8c', // Placeholder cinza
        activeBorderColor: '#52c41a', // Borda ativa verde
        hoverBorderColor: '#73d13d', // Borda hover verde claro
        // Configurações para desabilitar autofill styling
        autocompleteBg: '#ffffff',
      },
      Select: {
        colorBgContainer: '#ffffff', // Select branco
        colorBorder: '#d9f7be', // Borda verde claro
        colorText: '#262626', // Texto escuro
        optionSelectedBg: '#d9f7be', // Opção selecionada verde claro
        optionActiveBg: '#f6ffed', // Opção ativa verde muito claro
      },
      Modal: {
        contentBg: '#ffffff', // Modal branco
        headerBg: '#f6ffed', // Header do modal verde muito claro
        maskBg: 'rgba(82, 196, 26, 0.45)', // Máscara com verde transparente
      },
      Button: {
        colorPrimary: '#52c41a', // Botío primário verde
        colorPrimaryHover: '#73d13d', // Hover verde claro
        colorPrimaryActive: '#389e0d', // Ativo verde escuro
        primaryShadow: '0 2px 0 rgba(82, 196, 26, 0.1)', // Sombra verde
      },
      Notification: {
        colorBgElevated: '#ffffff', // Notificação branca
        colorBorder: '#d9f7be', // Borda verde claro
      },
      Drawer: {
        colorBgElevated: '#ffffff', // Drawer branco
        colorBgMask: 'rgba(82, 196, 26, 0.45)', // Máscara verde transparente
      },
      Dropdown: {
        colorBgElevated: '#ffffff', // Dropdown branco
        colorBorder: '#d9f7be', // Borda verde claro
      },
    },
  },
}

// Provider do tema
export const ThemeProvider = ({ children }) => {
  // Recuperar tema salvo do localStorage ou usar 'light' como padrío
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('Etiquetando-theme')
    // Se não há tema salvo, definir light como padrío
    if (!savedTheme) {
      localStorage.setItem('Etiquetando-theme', 'light')
      return 'light'
    }
    return savedTheme
  })

  // Salvar tema no localStorage quando mudar
  useEffect(() => {
    localStorage.setItem('Etiquetando-theme', currentTheme)

    // Aplicar classe CSS no body para estilos customizados
    if (currentTheme === 'dark') {
      document.body.classList.add('theme-dark')
      document.body.classList.remove('theme-light')
    } else {
      document.body.classList.add('theme-light')
      document.body.classList.remove('theme-dark')
    }
  }, [currentTheme])

  // Função para alternar tema
  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'dark' ? 'light' : 'dark')
  }

  // Função para definir tema específico
  const setTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName)
    }
  }

  const value = {
    theme: currentTheme,
    currentTheme,
    themes: Object.keys(themes),
    toggleTheme,
    setTheme,
    isDark: currentTheme === 'dark',
    isLight: currentTheme === 'light',
  }

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        locale={ptBR}
        theme={themes[currentTheme]}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  )
}

export default ThemeProvider